import Color from 'color';
import {
  borderColor,
  borderColor2,
  LEFT_SIDE_PANEL_MOTION_MS,
  leftSidePanelBodyMotion,
  leftSidePanelMotionEase,
  leftSidePanelWidthMotion,
  logRes1,
  mobileRes2,
} from 'pages/styles';
import styled from 'styled-components';

export const scrollBarRes = '1150px';
const lFColor = '#490c7b';
const lFColor0 = '#7914cc';
const lFColor2 = '#212137';
const checkPurple = lFColor0;
const checkPurpleMuted = Color(lFColor0).lighten(0.28).desaturate(0.15).toString();
const orange = Color('#d6a615').darken(0).toString();
const ink = '#1a1a1a';
const inkStrong = '#2a2a2a';
const inkBody = '#4a4a4a';
const inkMuted = '#6e6e6e';
const inkFaint = '#8a8a8a';

const panelRightShadow = '1px 0 0 rgba(17, 24, 39, 0.06), 4px 0 16px rgba(17, 24, 39, 0.04)';
const panelBg = '#fafafa';
const line = Color('#e5e7eb').darken(0.02).toString();
const rowHover = Color(lFColor0).alpha(0.06).toString();
const rowSelected = Color(lFColor0).alpha(0.1).toString();

export const AuditsCont = styled.div<{ isWeb?: boolean, $open?: boolean, $fullyHidden?: boolean }>`
  ${leftSidePanelWidthMotion}
  box-sizing: border-box;
  background: ${panelBg};
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  z-index: 3;
  position: relative;
  box-shadow: ${panelRightShadow};
  ${({ $fullyHidden, isWeb }) => ($fullyHidden && isWeb ? `
    width: 0;
    min-width: 0;
    flex-basis: 0;
    box-shadow: none;
    pointer-events: none;
    opacity: 0;
  ` : '')}
  transition:
    width ${LEFT_SIDE_PANEL_MOTION_MS}ms ${leftSidePanelMotionEase},
    min-width ${LEFT_SIDE_PANEL_MOTION_MS}ms ${leftSidePanelMotionEase},
    flex-basis ${LEFT_SIDE_PANEL_MOTION_MS}ms ${leftSidePanelMotionEase},
    opacity ${LEFT_SIDE_PANEL_MOTION_MS}ms ${leftSidePanelMotionEase},
    box-shadow ${LEFT_SIDE_PANEL_MOTION_MS}ms ${leftSidePanelMotionEase};
  @media(max-width: ${logRes1}) {
    /* Desktop duplicate must fully unmount from layout — left:-100% still peeked
       (enter transforms / flex shrink made a thin right-aligned strip). */
    display: ${({ isWeb }) => (isWeb ? 'none' : 'flex')};
    box-shadow: ${({ isWeb }) => isWeb && 'none'};
    background: white;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const PanelToggleRow = styled.div<{ $collapsed?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: ${({ $collapsed }) => ($collapsed ? '14px 10px' : '14px 14px 12px')};
  box-sizing: border-box;
  border-bottom: 1px solid ${({ $collapsed }) => ($collapsed ? 'transparent' : line)};
  background: ${({ $collapsed }) => ($collapsed ? panelBg : 'white')};
  flex-shrink: 0;
  transition:
    padding ${LEFT_SIDE_PANEL_MOTION_MS}ms ${leftSidePanelMotionEase},
    border-color 220ms ${leftSidePanelMotionEase},
    background 220ms ${leftSidePanelMotionEase};
  @media(max-width: ${logRes1}) {
    display: none;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const PanelTitle = styled.div<{ $hidden?: boolean }>`
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${ink};
  line-height: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
  pointer-events: ${({ $hidden }) => ($hidden ? 'none' : 'auto')};
  transition: opacity 220ms ${leftSidePanelMotionEase};
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const PanelToggle = styled.button<{ $expanded?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border: 1px solid ${line};
  border-radius: 8px;
  background: #fff;
  color: ${inkMuted};
  cursor: pointer;
  padding: 0;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  & > svg {
    transition: transform ${LEFT_SIDE_PANEL_MOTION_MS}ms ${leftSidePanelMotionEase};
    transform: rotate(${({ $expanded }) => ($expanded === false ? 180 : 0)}deg);
  }

  &:hover {
    background: ${rowHover};
    border-color: ${Color(lFColor0).alpha(0.28).toString()};
    color: ${lFColor0};
  }

  @media (prefers-reduced-motion: reduce) {
    & > svg {
      transition: none;
    }
  }
`;

export const PaneBody = styled.div<{ $open?: boolean }>`
  ${leftSidePanelBodyMotion}
`;

export const SearchCont = styled.div<{ $clearEscape?: boolean; $belowPanelTitle?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  min-height: 50px;
  /* Slide-in panel: keep search below the absolute Escape (X) button. */
  padding: ${({ $clearEscape, $belowPanelTitle }) => {
    if ($clearEscape) return '52px 12px 12px 12px';
    if ($belowPanelTitle) return '10px 12px 12px 12px';
    return '14px 12px 12px 12px';
  }};
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: white;
  border-bottom: 1px solid ${line};
`;

export const SearchRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

export const SearchInputWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
`;

export const IconDiv = styled.div<{ isActive: boolean }>`
  position: absolute;
  top: 50%;
  left: 11px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  color: ${inkMuted};
  color: ${({ isActive }) => isActive && 'white'};
  pointer-events: none;
  z-index: 1;

  & > svg {
    font-size: 20px;
  }
`;

export const Input = styled.input<{ isActive: boolean; $hasClear?: boolean; $hideLeftIcon?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  height: 34px;
  padding-left: ${({ $hideLeftIcon }) => ($hideLeftIcon ? '10px' : '40px')};
  padding-right: ${({ $hasClear }) => ($hasClear ? '36px' : '9px')};
  border-radius: 8px;
  border: 1px solid ${borderColor2};
  outline: 0;
  font-size: 12px;
  color: ${inkStrong};
  font-family: Poppins, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;
  font-weight: ${({ isActive }) => (isActive ? 600 : 300)};
  background: ${({ isActive }) => (isActive ? lFColor2 : 'white')};
  color: ${({ isActive }) => (isActive ? 'white' : inkStrong)};
  font-size: ${({ isActive }) => isActive && '14px'};

  &::placeholder {
    color: ${inkFaint};
  }
`;

export const ClearSearchBtn = styled.button<{ $isActive?: boolean }>`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  padding: 0;
  cursor: pointer;
  background: ${({ $isActive }) => (
    $isActive ? 'rgba(255, 255, 255, 0.18)' : 'rgba(0, 0, 0, 0.08)'
  )};
  color: ${({ $isActive }) => ($isActive ? 'white' : inkMuted)};
  z-index: 1;

  &:hover {
    background: ${({ $isActive }) => (
    $isActive ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.14)'
  )};
  }

  & > svg {
    font-size: 16px;
  }
`;

export const AddSearchBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1.5px solid ${borderColor2};
  background: white;
  color: ${borderColor};
  cursor: pointer;
  padding: 0;
  transition: background 120ms ease, color 120ms ease, border-color 120ms ease;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
    border-color: ${borderColor};
    color: ${Color(borderColor).darken(0.2).toString()};
  }

  & > svg {
    font-size: 22px;
  }
`;

const negateActive = '#b42318';

const optionsAccent = '#3d5a80';
const optionsAccentHot = '#2c4a6e';
const chipAddedNegateBg = Color(negateActive).lighten(0.05).toString();
const chipPurpleIdleBg = Color(lFColor0).lighten(0.86).toString();
const chipPurpleIdleBorder = Color(lFColor0).lighten(0.72).toString();
const chipPurpleActiveBg = Color(lFColor0).lighten(0.8).toString();
const chipNegateIdleBg = Color(negateActive).lighten(0.78).toString();
const chipNegateIdleBorder = Color(negateActive).lighten(0.55).toString();
const chipNegateIdleFg = Color(negateActive).darken(0.22).toString();

export const SearchOptionsBtn = styled.button<{ $open?: boolean; $custom?: boolean; $compact?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${({ $compact }) => ($compact ? '22px' : '34px')};
  height: ${({ $compact }) => ($compact ? '22px' : '34px')};
  border-radius: ${({ $compact }) => ($compact ? '50%' : '8px')};
  border: ${({ $compact, $custom, $open }) => {
    if ($compact) return 'none';
    if ($custom) return `1.5px solid ${optionsAccent}`;
    if ($open) return `1.5px solid ${borderColor}`;
    return `1.5px solid ${borderColor2}`;
  }};
  background: ${({ $custom, $open, $compact }) => {
    if ($custom) return optionsAccent;
    if ($compact) return 'rgba(255, 255, 255, 0.14)';
    if ($open) return 'rgba(0, 0, 0, 0.04)';
    return 'white';
  }};
  color: ${({ $custom, $compact }) => {
    if ($custom) return 'white';
    if ($compact) return 'white';
    return borderColor;
  }};
  cursor: pointer;
  padding: 0;
  transition: background 120ms ease, color 120ms ease, border-color 120ms ease;

  &:hover {
    background: ${({ $custom, $compact }) => {
    if ($custom) return optionsAccentHot;
    if ($compact) return 'rgba(255, 255, 255, 0.28)';
    return 'rgba(0, 0, 0, 0.04)';
  }};
    border-color: ${({ $custom, $compact }) => {
    if ($compact) return 'transparent';
    if ($custom) return optionsAccentHot;
    return borderColor;
  }};
    color: ${({ $custom, $compact }) => {
    if ($custom || $compact) return 'white';
    return Color(borderColor).darken(0.2).toString();
  }};
  }

  & > svg {
    font-size: ${({ $compact }) => ($compact ? '14px' : '20px')};
  }
`;

export const SearchOptionsAnchor = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

export const SearchOptionsPopup = styled.div`
  position: fixed;
  z-index: 1300;
  width: min(292px, calc(100vw - 24px));
  padding: 10px 10px 12px;
  background: white;
  border: 1px solid ${Color(borderColor2).lighten(0.05).toString()};
  border-radius: 8px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.14), 0 0 0 1px rgba(0, 0, 0, 0.04);
`;

export const SearchOptionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const SearchOptionsSectionLabel = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${inkFaint};
  margin: 4px 4px 6px;
`;

export const SearchOptionsDivider = styled.div`
  height: 1px;
  background: ${Color('lightgrey').lighten(0.08).toString()};
  margin: 8px 2px;
`;

export const SearchOptionsClearBtn = styled.button<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin-top: 4px;
  padding: 8px 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${({ $disabled }) => ($disabled ? inkFaint : negateActive)};
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
  transition: background 100ms ease;

  &:hover {
    background: ${({ $disabled }) => (
    $disabled ? 'transparent' : Color(negateActive).alpha(0.08).toString()
  )};
  }

  & > svg {
    font-size: 16px;
  }
`;

export const SearchOptionRow = styled.button<{ $checked?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  text-align: left;
  border: none;
  border-radius: 6px;
  padding: 8px 8px;
  background: ${({ $checked }) => (
    $checked ? Color(optionsAccent).alpha(0.1).toString() : 'transparent'
  )};
  color: ${inkStrong};
  cursor: pointer;
  transition: background 100ms ease;

  &:hover {
    background: ${({ $checked }) => (
    $checked
      ? Color(optionsAccent).alpha(0.14).toString()
      : 'rgba(0, 0, 0, 0.035)'
  )};
  }
`;

export const SearchOptionCheck = styled.span<{ $checked?: boolean; $radio?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-top: 1px;
  border-radius: ${({ $radio }) => ($radio ? '50%' : '3px')};
  border: 1.5px solid ${({ $checked }) => (
    $checked ? optionsAccent : Color(borderColor2).darken(0.05).toString()
  )};
  background: ${({ $checked }) => ($checked ? optionsAccent : 'white')};
  color: white;

  & > svg {
    font-size: 12px;
  }
`;

export const SearchOptionIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  margin-top: 0;
  color: ${inkMuted};

  & > svg {
    font-size: 18px;
  }
`;

export const SearchOptionText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  flex: 1;
`;

export const SearchOptionTitle = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.25;
  color: ${inkStrong};
`;

export const SearchOptionHint = styled.span`
  font-size: 10.5px;
  font-weight: 400;
  line-height: 1.3;
  color: ${inkMuted};
`;

export const SearchChips = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

export const SearchChip = styled.div<{ $negate?: boolean; $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  min-height: 34px;
  padding: 6px 8px 6px 10px;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: default;
  transition: background 120ms ease, color 120ms ease, border-color 120ms ease;

  ${({ $negate, $active }) => {
    if ($negate && $active) {
      return `
        background: ${chipAddedNegateBg};
        color: white;
        border-color: transparent;
      `;
    }
    if ($negate) {
      return `
        background: ${chipNegateIdleBg};
        color: ${chipNegateIdleFg};
        border-color: ${chipNegateIdleBorder};

        & > span {
          opacity: 1;
          color: ${chipNegateIdleFg};
        }

        & > button {
          background: rgba(180, 35, 24, 0.16);
          color: ${chipNegateIdleFg};
        }

        & > button:hover {
          background: rgba(180, 35, 24, 0.26);
          color: ${chipNegateIdleFg};
        }
      `;
    }
    if ($active) {
      return `
        background: ${chipPurpleActiveBg};
        color: ${lFColor};
        border-color: ${chipPurpleIdleBorder};
      `;
    }
    return `
      background: ${chipPurpleIdleBg};
      color: ${lFColor};
      border-color: ${chipPurpleIdleBorder};
    `;
  }}

  ${SearchOptionsBtn} {
    ${({ $negate, $active }) => {
    if ($negate && $active) {
      return `
        background: rgba(255, 255, 255, 0.14);
        color: white;
        &:hover { background: rgba(255, 255, 255, 0.28); color: white; }
      `;
    }
    if ($negate) {
      return `
        background: ${negateActive};
        color: #fff !important;
        &:hover { background: ${Color(negateActive).darken(0.12).toString()}; color: #fff !important; }
      `;
    }
    return `
      background: rgba(121, 20, 204, 0.1);
      color: ${lFColor};
      &:hover { background: rgba(121, 20, 204, 0.18); color: ${lFColor}; }
    `;
  }}
  }
`;

export const SearchChipIcon = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  opacity: 0.9;

  & > svg {
    font-size: 16px;
  }
`;

export const SearchChipText = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SearchChipInput = styled.input`
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: none;
  outline: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.3;
  caret-color: currentColor;

  &::placeholder {
    color: currentColor;
    opacity: 0.45;
  }
`;

export const SearchChipRemove = styled.button<{ $solid?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 50%;
  background: ${({ $solid }) => (
    $solid ? 'rgba(255, 255, 255, 0.14)' : 'rgba(0, 0, 0, 0.08)'
  )};
  color: ${({ $solid }) => ($solid ? 'white' : 'inherit')};
  cursor: pointer;
  padding: 0;

  &:hover {
    background: ${({ $solid }) => (
    $solid ? 'rgba(255, 255, 255, 0.28)' : 'rgba(0, 0, 0, 0.14)'
  )};
  }

  & > svg {
    font-size: 15px;
  }
`;

export const Header = styled.div`
  width: 100%;
  box-sizing: border-box;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 4px 14px 12px;
  color: ${ink};
  text-align: left;
`;

/* No top padding: sticky group headers must sit flush with this scrollport.
   Top inset lives on AuditsTopSpacer so rows cannot scroll through a padding gap. */
export const Audits = styled.div`
  flex: 1;
  padding: 0 10px 24px;
  box-sizing: border-box;
  color: ${inkBody};
  overflow-y: auto;
  position: relative;
  @media(max-width: ${mobileRes2}) {
    padding: 0 12px 24px;
  }
`;

export const AuditsTopSpacer = styled.div`
  width: 100%;
  height: 10px;
  flex-shrink: 0;
`;

export const AuditCont = styled.div<{ isCollapsed?: boolean }>`
  width: 100%;
  padding-bottom: ${({ isCollapsed }) => (isCollapsed ? '8px' : '16px')};

  & + & {
    margin-top: 2px;
  }
`;

export const AuditGroup1 = styled.div<{ $selected?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  font-weight: ${({ $selected }) => ($selected ? 600 : 500)};
  font-size: 12.5px;
  color: ${({ $selected }) => ($selected ? lFColor0 : inkStrong)};
  padding: 9px 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  border-radius: 8px;
  background: ${({ $selected }) => ($selected ? rowSelected : 'transparent')};
  transition: background 0.12s, color 0.12s;

  &:hover {
    background: ${({ $selected }) => ($selected ? rowSelected : rowHover)};
    color: ${lFColor0};
  }
  @media(max-width: ${mobileRes2}) {
    font-size: 12.5px;
  }
`;

export const AuditGroup0 = styled(AuditGroup1)`
  padding: 9px 15px;
  color: white;
  font-weight: 700;
  background: ${Color(lFColor).lighten(0.2).toString()};
  border-radius: 8px;
  font-size: 12px;
  margin-top: 5px;
  margin-bottom: 12px;
  &:hover {
    color: white;
    background: ${Color(orange).lighten(0.2).toString()};
  }
`;

export const AuditGroup = styled(AuditGroup1)`
  align-items: center;
`;

export const AuditGroupLabel = styled.span`
  flex: 1;
  min-width: 0;
  line-height: 1.3;
`;

export const AuditGroupSticky = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  background: ${panelBg};
  /* Bleed into side padding so the stuck bar is the full width of the list. */
  margin-left: -10px;
  margin-right: -10px;
  padding: 4px 10px;
  box-shadow: 0 1px 0 ${line};

  ${AuditGroup} {
    background: white;
  }

  @media(max-width: ${mobileRes2}) {
    margin-left: -12px;
    margin-right: -12px;
    padding-left: 12px;
    padding-right: 12px;
  }
`;

export const GroupTypeSearch = styled.div`
  width: 100%;
  box-sizing: border-box;
  margin-top: 4px;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 7px 10px;
  border: 1px solid ${line};
  border-radius: 8px;
  background: white;
  color: ${inkMuted};
  transition: border-color 120ms ease, background 120ms ease, color 120ms ease;

  &:focus-within {
    border-color: ${Color(lFColor0).alpha(0.35).toString()};
    background: white;
    color: ${inkBody};
  }

  & > svg {
    font-size: 14px;
    flex-shrink: 0;
  }
`;

export const GroupTypeSearchInput = styled.input`
  width: 100%;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 11.5px;
  font-weight: 400;
  color: ${inkStrong};
  font-family: Poppins, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;
  padding: 0;
  line-height: 1.3;

  &::placeholder {
    color: ${inkFaint};
    font-weight: 400;
  }
`;

export const Select = styled.input`
  height: 16px;
  width: 16px;
  margin: 0;
  flex-shrink: 0;
  pointer-events: none;
  accent-color: ${checkPurple};
`;

export const CheckBox = styled.span<{ $state: 'off' | 'partial' | 'on' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  height: 16px;
  width: 16px;
  margin: 0;
  flex-shrink: 0;
  border-radius: 4px;
  border: 1.5px solid
    ${({ $state }) => {
    if ($state === 'on') return checkPurple;
    if ($state === 'partial') return checkPurpleMuted;
    return Color(borderColor2).darken(0.05).toString();
  }};
  background: ${({ $state }) => {
    if ($state === 'on') return checkPurple;
    if ($state === 'partial') return checkPurpleMuted;
    return 'white';
  }};
  color: white;
  pointer-events: none;

  & > svg {
    font-size: 12px;
  }
`;

export const DropDownMarker = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-left: auto;
  padding: 2px 4px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${inkMuted};
  cursor: pointer;
  font: inherit;
  line-height: 1;
  flex-shrink: 0;

  &:hover {
    color: ${lFColor0};
    background: ${rowHover};
  }

  & > svg {
    font-size: 18px;
  }
`;

export const DropDownCount = styled.span`
  font-size: 11px;
  font-weight: 600;
  min-width: 1.5em;
  text-align: right;
  color: inherit;
  white-space: nowrap;
`;

export const Select2 = styled(Select)``;

export const AuditName = styled.div<{ isDeleted?: boolean; isSelected?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  font-size: 12.5px;
  font-weight: ${({ isSelected }) => (isSelected ? 600 : 400)};
  color: ${({ isDeleted, isSelected }) => {
    if (isDeleted) return inkFaint;
    if (isSelected) return lFColor0;
    return inkBody;
  }};
  padding: 8px 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  border-radius: 8px;
  background: ${({ isSelected }) => (isSelected ? rowSelected : 'transparent')};
  transition: background 0.12s, color 0.12s;
  ${AuditGroupSticky} + & {
    margin-top: 2px;
  }
  &:hover {
    background: ${({ isSelected }) => (isSelected ? rowSelected : rowHover)};
    color: ${({ isDeleted }) => (isDeleted ? inkMuted : lFColor0)};
  }
`;

export const AuditNameText = styled.span<{ isDeleted?: boolean }>`
  flex: 1;
  min-width: 0;
  line-height: 1.3;
  text-decoration: ${({ isDeleted }) => (isDeleted ? 'line-through' : 'none')};
  text-decoration-thickness: 1px;
`;

export const DeletedIcon = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: ${inkFaint};
  line-height: 1;
`;

export const EsacpePart = styled.div`
  display: none;
  width: 100%;
  height: 0px;
  overflow: visible;
  position: absolute;
  top: 0;
  right: 0;
  @media(max-width: ${logRes1}) {
    display: block;
  }
`;

/** Combine SKU — rolls up value logs by type in the Audits table. */
export const CombineSkuToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: calc(100% - 28px);
  margin: 0 14px 10px;
  padding: 8px 10px;
  border: 1px solid ${line};
  border-radius: 8px;
  background: #fff;
  color: ${inkBody};
  font-size: 12px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  text-align: left;
  box-sizing: border-box;
  &:hover {
    background: ${rowHover};
    border-color: ${Color(lFColor0).alpha(0.35).toString()};
  }
`;

export const CombineSkuCheck = styled.input`
  width: 14px;
  height: 14px;
  margin: 0;
  accent-color: ${lFColor0};
  pointer-events: none;
`;
